"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  NumberInput,
  SelectInput,
  ResultBanner,
  GuardedPrintButton,
  CompletionPanel,
  SegmentedControl,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
  type Verdict,
} from "@/components/tools/ui";

/* TILE manual handling assessment (Manual Handling Operations Regulations 1992).
   This is a FILTER-LEVEL screen, not the HSE MAC tool and not a substitute for a
   competent assessor. Each "yes" to a risk-factor prompt adds to that section's
   score; the highest section score sets the overall band.

   Guideline weights come from the HSE lifting/lowering guideline figures (L23
   Appendix / the simple risk filters). They are GUIDELINE FIGURES, not legal
   limits — UK manual handling law sets no fixed weight limit. Exceeding the
   relevant figure means "assess in more detail", never "illegal".
   Canonical close-to-body figures (man / woman):
     - waist / knuckle height: 25 kg / 16 kg
     - above shoulder or below knee (floor): 10 kg / 7 kg
     - elbow / knee height: 20 kg / 13 kg
     - seated handling: 5 kg / 3 kg
   Holding the load away from the body roughly halves the relevant figure. */

type Answer = "no" | "partly" | "yes";

const ANSWER_OPTIONS: { value: Answer; label: string }[] = [
  { value: "no", label: "No" },
  { value: "partly", label: "Partly" },
  { value: "yes", label: "Yes" },
];

// A "yes" scores 2, "partly" scores 1, "no" scores 0.
function answerScore(a: Answer): number {
  return a === "yes" ? 2 : a === "partly" ? 1 : 0;
}

interface Prompt {
  id: string;
  text: string;
}

interface SectionDef {
  key: "task" | "individual" | "load" | "environment";
  letter: string;
  title: string;
  blurb: string;
  prompts: Prompt[];
}

const SECTIONS: SectionDef[] = [
  {
    key: "task",
    letter: "T",
    title: "Task",
    blurb: "How the load is moved — the job itself.",
    prompts: [
      { id: "t_twist", text: "Does it involve twisting or stooping?" },
      { id: "t_away", text: "Is the load held away from the body?" },
      { id: "t_carry", text: "Is there a long carrying distance (more than 10 m)?" },
      { id: "t_repeat", text: "Is the lifting frequent or repetitive?" },
      { id: "t_pushpull", text: "Does it involve strenuous pushing or pulling?" },
      { id: "t_height", text: "Is handling done above shoulder or below knee height?" },
      { id: "t_team", text: "Is team handling needed to move the load?" },
    ],
  },
  {
    key: "individual",
    letter: "I",
    title: "Individual",
    blurb: "The person doing the handling.",
    prompts: [
      { id: "i_strength", text: "Does the task require unusual strength or height?" },
      { id: "i_vulnerable", text: "Is the worker a new/expectant mother, young or inexperienced?" },
      { id: "i_health", text: "Does the worker have an existing injury or health condition?" },
      { id: "i_training", text: "Are there gaps in manual handling training?" },
      { id: "i_ppe", text: "Does PPE restrict movement or grip?" },
    ],
  },
  {
    key: "load",
    letter: "L",
    title: "Load",
    blurb: "The object being handled.",
    prompts: [
      { id: "l_heavy", text: "Is the load heavy? (capture the approximate weight below)" },
      { id: "l_bulky", text: "Is it bulky or unwieldy?" },
      { id: "l_grip", text: "Is it difficult to grip?" },
      { id: "l_unstable", text: "Are the contents unstable or liable to shift?" },
      { id: "l_sharp", text: "Does it have sharp, hot or otherwise hazardous edges?" },
    ],
  },
  {
    key: "environment",
    letter: "E",
    title: "Environment",
    blurb: "Where the handling happens.",
    prompts: [
      { id: "e_floor", text: "Are floors uneven, wet or slippery?" },
      { id: "e_steps", text: "Are there steps or slopes on the route?" },
      { id: "e_space", text: "Are there space constraints (cramped, restricted posture)?" },
      { id: "e_light", text: "Is lighting poor?" },
      { id: "e_temp", text: "Is the temperature, ventilation or weather extreme?" },
      { id: "e_levels", text: "Are there variations in floor levels along the route?" },
    ],
  },
];

/* Pushing / pulling branch. These only appear (and only score) when the Task
   "strenuous pushing or pulling" prompt is flagged, so they cost nothing for a
   straight lift but surface the extra HSE push/pull factors when relevant. */
const PUSHPULL_PROMPTS: Prompt[] = [
  { id: "pp_force", text: "Is the force to start or keep the load moving high (e.g. above the HSE guideline of ~20 kg / 10 kg force to start)?" },
  { id: "pp_slope", text: "Is the load pushed or pulled up a slope, over a kerb/threshold, or on poor wheels/castors?" },
  { id: "pp_posture", text: "Is the load handled below knuckle or above shoulder height, or with the hands away from the body?" },
];

type Answers = Record<string, Answer>;

const initialAnswers: Answers = SECTIONS.reduce((acc, s) => {
  s.prompts.forEach((p) => (acc[p.id] = "no"));
  PUSHPULL_PROMPTS.forEach((p) => (acc[p.id] = "no"));
  return acc;
}, {} as Answers);

// ── Guideline weight model ──────────────────────────────────────────────────
type Zone = "waist" | "elbowknee" | "floorshoulder" | "seated";

const ZONE_LABELS: Record<Zone, string> = {
  waist: "Waist / knuckle height (the easiest zone)",
  elbowknee: "Elbow or knee height",
  floorshoulder: "Above shoulder or below knee / floor",
  seated: "Handling while seated",
};

// Close-to-body guideline figures (man, woman) in kg, from the HSE
// lifting/lowering guideline figures (L23 Appendix / simple risk filters).
const ZONE_FIGURES: Record<Zone, { man: number; woman: number }> = {
  waist: { man: 25, woman: 16 },
  elbowknee: { man: 20, woman: 13 },
  floorshoulder: { man: 10, woman: 7 },
  seated: { man: 5, woman: 3 },
};

type Sex = "man" | "woman";

function guidelineFigure(zone: Zone, sex: Sex, awayFromBody: boolean): number {
  const base = ZONE_FIGURES[zone][sex];
  // Holding a load away from the body roughly halves the guideline figure.
  return awayFromBody ? Math.round(base / 2) : base;
}

// ── Banding ─────────────────────────────────────────────────────────────────
function bandFromScore(score: number, max: number): Verdict {
  if (max === 0) return "low";
  const ratio = score / max;
  if (ratio >= 0.6) return "veryhigh";
  if (ratio >= 0.35) return "high";
  if (ratio > 0) return "medium";
  return "low";
}

const BAND_LABEL: Record<Verdict, string> = {
  low: "Low",
  medium: "Some risk factors",
  high: "Elevated risk",
  veryhigh: "High risk",
};

function maxOfBands(bands: Verdict[]): Verdict {
  const order: Verdict[] = ["low", "medium", "high", "veryhigh"];
  return bands.reduce((acc, b) => (order.indexOf(b) > order.indexOf(acc) ? b : acc), "low");
}

export function ManualHandlingAssessment() {
  const [task, setTask] = useState("");
  const [location, setLocation] = useState("");
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();

  const [answers, setAnswers] = useState<Answers>(initialAnswers);

  const [weight, setWeight] = useState("");
  const [sex, setSex] = useState<Sex>("man");
  const [zone, setZone] = useState<Zone>("waist");

  const resultTracked = useRef(false);

  const setAnswer = (id: string, value: Answer) =>
    setAnswers((a) => ({ ...a, [id]: value }));

  const awayFromBody = answers["t_away"] === "yes" || answers["t_away"] === "partly";
  // The push/pull branch is active (visible and scored) only when strenuous
  // pushing or pulling has been flagged on the Task section.
  const pushPullActive = answers["t_pushpull"] !== "no";

  const computed = useMemo(() => {
    const sections = SECTIONS.map((s) => {
      // Fold the push/pull follow-ups into the Task section when they apply.
      const prompts = s.key === "task" && pushPullActive ? [...s.prompts, ...PUSHPULL_PROMPTS] : s.prompts;
      const score = prompts.reduce((sum, p) => sum + answerScore(answers[p.id]), 0);
      const max = prompts.length * 2;
      const flagged = prompts.filter((p) => answers[p.id] !== "no");
      return { def: s, score, max, band: bandFromScore(score, max), flagged };
    });

    const overall = maxOfBands(sections.map((s) => s.band));

    // Weight vs guideline figure.
    const w = parseFloat(weight);
    let weightCheck: {
      weight: number;
      figure: number;
      over: boolean;
    } | null = null;
    if (isFinite(w) && w > 0) {
      const figure = guidelineFigure(zone, sex, awayFromBody);
      weightCheck = { weight: w, figure, over: w > figure };
    }

    return { sections, overall, weightCheck };
  }, [answers, weight, sex, zone, awayFromBody]);

  // Suggested controls driven by which factors are flagged.
  const controls = useMemo(() => {
    const out: string[] = [];
    const yes = (id: string) => answers[id] === "yes" || answers[id] === "partly";

    if (computed.weightCheck?.over || yes("l_heavy") || yes("t_team")) {
      out.push("Use a mechanical aid (sack truck, trolley, hoist, vacuum lifter) so the load is not carried by hand.");
    }
    if (yes("t_team") || yes("l_heavy") || yes("l_bulky")) {
      out.push("Plan a team lift with a nominated lead, or break the load down into smaller, lighter units.");
    }
    if (yes("t_carry") || yes("e_steps") || yes("e_levels") || yes("e_floor")) {
      out.push("Improve the route — shorten the carry, clear and level the floor, use a lift or ramp instead of steps.");
    }
    if (yes("t_height") || yes("t_twist") || yes("t_away")) {
      out.push("Reorganise storage and work heights so loads are picked up and set down between knee and shoulder, close to the body, without twisting.");
    }
    if (yes("i_training") || yes("i_strength") || yes("t_repeat")) {
      out.push("Provide manual handling training and build in rest breaks or job rotation for repetitive handling.");
    }
    if (yes("i_vulnerable") || yes("i_health")) {
      out.push("Match the task to the individual — adjust the load or task for new/expectant mothers, younger, inexperienced or injured workers.");
    }
    if (yes("l_grip") || yes("l_unstable") || yes("l_sharp") || yes("i_ppe")) {
      out.push("Make the load safer to hold — handles or grips, secured contents, edge protection, and PPE that does not restrict movement.");
    }
    if (yes("e_light") || yes("e_temp") || yes("e_space")) {
      out.push("Fix the environment — improve lighting, allow more working space, and manage temperature, ventilation or weather exposure.");
    }
    if (yes("t_pushpull") || yes("pp_force") || yes("pp_slope") || yes("pp_posture")) {
      out.push("For pushing/pulling, cut the force needed first — well-maintained wheels/castors and floors, gentler gradients and ramps over kerbs, handles at waist height, and a powered or assisted mover for heavy or repeated moves.");
    }
    return out;
  }, [answers, computed.weightCheck]);

  const today = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const anyAnswered = useMemo(
    () =>
      SECTIONS.some((s) => s.prompts.some((p) => answers[p.id] !== "no")) ||
      (isFinite(parseFloat(weight)) && parseFloat(weight) > 0),
    [answers, weight],
  );

  // Fire the funnel event once per visit when a result first appears.
  useEffect(() => {
    if (anyAnswered && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("manual-handling-assessment", { level: computed.overall });
    }
  }, [anyAnswered, computed.overall]);

  // Core fields a competent person would expect on a TILE screen: who/what, and
  // either the risk-factor prompts worked through or a load weight checked. The
  // detailed prompts stay optional — this is a filter, not a full assessment.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Task being assessed", done: task.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
    {
      label: "TILE prompts worked through, or a load weight entered",
      done: anyAnswered,
    },
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="space-y-6">
        {/* Header details */}
        <div className="card shadow-soft p-6 print-hide">
          <h2 className="text-xl">Assessment details</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Task being assessed" className="sm:col-span-2">
              <TextInput
                value={task}
                onChange={(e) => setTask(e.target.value)}
                placeholder="e.g. unloading 25 kg cement bags from a van to store"
              />
            </Field>
            <Field label="Location / site">
              <TextInput value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Site or area" />
            </Field>
          </div>
        </div>

        {/* TILE sections */}
        {SECTIONS.map((s) => {
          const section = computed.sections.find((c) => c.def.key === s.key)!;
          return (
            <div key={s.key} className="card shadow-soft p-6 print-hide">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="grid h-7 w-7 place-items-center rounded-md bg-emerald-tint font-display text-sm font-bold text-emerald-deep">
                      {s.letter}
                    </span>
                    <h2 className="text-xl">{s.title}</h2>
                  </div>
                  <p className="mt-1 text-sm text-muted">{s.blurb}</p>
                </div>
                <span className="shrink-0 rounded-full border border-line px-2.5 py-1 text-[0.72rem] font-semibold uppercase tracking-wide text-muted">
                  {BAND_LABEL[section.band]}
                </span>
              </div>

              <div className="mt-4 space-y-2.5">
                {s.prompts.map((p) => (
                  <div
                    key={p.id}
                    className="grid gap-2 rounded-lg border border-line bg-mist/50 px-3.5 py-3 sm:grid-cols-[1fr_auto] sm:items-center"
                  >
                    <span className="text-[0.92rem] leading-snug text-body">{p.text}</span>
                    <SegmentedControl
                      legend={p.text}
                      name={`mha-${p.id}`}
                      value={answers[p.id]}
                      onChange={(v) => setAnswer(p.id, v)}
                      options={ANSWER_OPTIONS.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                        activeCls:
                          opt.value === "yes"
                            ? "border-risk-high/50 bg-[#fef0e6] text-[#b34e10]"
                            : opt.value === "partly"
                              ? "border-risk-medium/50 bg-[#fdf6e3] text-[#8a6400]"
                              : "border-emerald/50 bg-emerald-tint text-emerald-deep",
                      }))}
                    />
                  </div>
                ))}
              </div>

              {/* Push/pull follow-ups appear in the Task section only when
                  strenuous pushing or pulling has been flagged. */}
              {s.key === "task" && pushPullActive && (
                <div className="mt-4 rounded-xl border border-line-2 bg-white p-4">
                  <div className="text-[0.85rem] font-semibold text-ink">Pushing / pulling — extra factors</div>
                  <p className="mt-1 text-[0.78rem] text-muted">
                    UK law sets no fixed force limit; these surface the main HSE push/pull factors. The aim is to
                    cut the force needed before relying on technique.
                  </p>
                  <div className="mt-3 space-y-2.5">
                    {PUSHPULL_PROMPTS.map((p) => (
                      <div
                        key={p.id}
                        className="grid gap-2 rounded-lg border border-line bg-mist/50 px-3.5 py-3 sm:grid-cols-[1fr_auto] sm:items-center"
                      >
                        <span className="text-[0.92rem] leading-snug text-body">{p.text}</span>
                        <SegmentedControl
                          legend={p.text}
                          name={`mha-${p.id}`}
                          value={answers[p.id]}
                          onChange={(v) => setAnswer(p.id, v)}
                          options={ANSWER_OPTIONS.map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                            activeCls:
                              opt.value === "yes"
                                ? "border-risk-high/50 bg-[#fef0e6] text-[#b34e10]"
                                : opt.value === "partly"
                                  ? "border-risk-medium/50 bg-[#fdf6e3] text-[#8a6400]"
                                  : "border-emerald/50 bg-emerald-tint text-emerald-deep",
                          }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Load weight check sits inside the Load section. */}
              {s.key === "load" && (
                <div className="mt-4 rounded-xl border border-line-2 bg-white p-4">
                  <div className="text-[0.85rem] font-semibold text-ink">Check the weight against the HSE guideline figure</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <Field label="Approx. weight (kg)">
                      <NumberInput
                        min={0}
                        step={0.5}
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        placeholder="e.g. 25"
                      />
                    </Field>
                    <Field label="Handler">
                      <SelectInput value={sex} onChange={(e) => setSex(e.target.value as Sex)}>
                        <option value="man">Average man</option>
                        <option value="woman">Average woman</option>
                      </SelectInput>
                    </Field>
                    <Field label="Lift zone">
                      <SelectInput value={zone} onChange={(e) => setZone(e.target.value as Zone)}>
                        {(Object.keys(ZONE_LABELS) as Zone[]).map((z) => (
                          <option key={z} value={z}>
                            {ZONE_LABELS[z]}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                  </div>
                  <p className="mt-2 text-[0.78rem] text-muted">
                    {awayFromBody
                      ? "Because the load is held away from the body, the guideline figure is roughly halved."
                      : "Figures assume the load is held close to the body. If it is held away from the body, the figure is roughly halved."}
                  </p>
                  {computed.weightCheck && (
                    <div
                      className={`mt-3 rounded-lg px-3.5 py-2.5 text-[0.85rem] font-medium ${
                        computed.weightCheck.over
                          ? "bg-[#fef0e6] text-[#b34e10]"
                          : "bg-emerald-tint text-emerald-deep"
                      }`}
                    >
                      {computed.weightCheck.weight} kg vs a {computed.weightCheck.figure} kg guideline figure for this zone —{" "}
                      {computed.weightCheck.over
                        ? "over the guideline figure, so assess this lift in more detail."
                        : "within the guideline figure for this zone."}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* Result + printable record */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Assessment completion" />
        </div>
        {anyAnswered ? (
          <>
            <div className="print-area card shadow-soft p-6" id="mha-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Manual handling assessment (TILE)
              </div>
              <h2 className="mt-1 text-lg">{task || "Untitled handling task"}</h2>
              <div className="mt-1 text-[0.82rem] text-muted">
                {evidence.preparedBy ? `${evidence.preparedBy} · ` : ""}
                {location ? `${location} · ` : ""}
                {today}
              </div>

              <div className="mt-4">
                <ResultBanner
                  level={computed.overall}
                  title={`Overall: ${BAND_LABEL[computed.overall]}`}
                >
                  {computed.overall === "low"
                    ? "No significant risk factors flagged at this filter level. Record the assessment and review if the task, load, people or place change."
                    : computed.overall === "veryhigh"
                      ? "Several significant risk factors. Reduce the risk before this task goes ahead, and use the HSE MAC tool or a competent assessor for a full assessment."
                      : "Risk factors that need controlling. Apply the suggested controls below; for anything heavy, awkward or repetitive, follow up with the HSE MAC tool or a competent assessor."}
                </ResultBanner>
              </div>

              {/* Per-section summary */}
              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
                    <th className="py-1.5 pr-2 font-semibold">Section</th>
                    <th className="py-1.5 pr-2 font-semibold">Level</th>
                    <th className="py-1.5 text-right font-semibold">Factors flagged</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.sections.map((c) => (
                    <tr key={c.def.key} className="border-b border-line/60 align-top">
                      <td className="py-2 pr-2 text-ink">
                        <span className="font-semibold">{c.def.letter}</span> · {c.def.title}
                      </td>
                      <td className="py-2 pr-2 text-ink">{BAND_LABEL[c.band]}</td>
                      <td className="py-2 text-right text-muted">
                        {c.flagged.length > 0 ? `${c.flagged.length} of ${c.def.prompts.length}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Weight line in the record */}
              {computed.weightCheck && (
                <div className="mt-3 rounded-lg bg-mist px-3.5 py-2.5 text-[0.82rem] text-ink">
                  Load weight {computed.weightCheck.weight} kg ·{" "}
                  {ZONE_LABELS[zone].toLowerCase()} · {sex === "man" ? "man" : "woman"} ·
                  guideline figure {computed.weightCheck.figure} kg —{" "}
                  <span className={computed.weightCheck.over ? "font-semibold text-[#b34e10]" : "font-semibold text-emerald-deep"}>
                    {computed.weightCheck.over ? "over guideline (assess in more detail)" : "within guideline"}
                  </span>
                </div>
              )}

              {/* Suggested controls */}
              {controls.length > 0 && (
                <div className="mt-4">
                  <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Suggested controls</div>
                  <ul className="mt-2 space-y-1.5">
                    {controls.map((c, i) => (
                      <li key={i} className="flex gap-2 text-[0.88rem] leading-snug text-body">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 rounded-lg border border-line bg-mist/60 px-3.5 py-2.5 text-[0.78rem] leading-relaxed text-muted">
                HSE guideline weights are not legal limits — UK manual handling law sets no fixed maximum weight.
                Exceeding a guideline figure means the lift should be assessed in more detail, not that it is illegal.
                This is a filter-level screen; heavy, awkward, repetitive or otherwise high-risk handling needs the
                HSE MAC tool or a competent assessor.
              </div>

              <EvidencePrint value={evidence} briefing />

              <PrintFooter toolName="manual handling assessment" />
            </div>
            <div className="print-hide">
              <GuardedPrintButton
                tool="manual-handling-assessment"
                label="Print / save record as PDF"
                missing={missingRequired}
                warningTitle="This manual handling assessment has missing core fields"
              />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the TILE prompts and enter a load weight to build a printable manual handling assessment record
            with suggested controls.
          </div>
        )}
      </div>
    </div>
  );
}
